import { FC, useEffect } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
import { get } from 'lodash';
// i18next
import { useTranslation } from 'react-i18next';
// Components
import { Button, IconButton } from 'components/Buttons';
import { Tooltip } from 'components/Tooltip';
import MultiLanguageTextField from 'components/MultiLanguageTextField';
// Icons
import { FaTrash } from "react-icons/fa";
// Utilities
import { isRequired, noWhitespace } from 'utilities/validation';

type Props = {
  name: string; // e.g. `registrationPage.fields.3.choices`
  fieldRef: string; // stable ref of the parent field, for translation keys
  itemLabel?: string; // e.g. "Option" or "Team" - prefixed to the row number
  minItems?: number; // below this count, remove is disabled (e.g. team needs >=1)
}

const ChoicesEditor:FC<Props> = ({ name, fieldRef, itemLabel, minItems = 0 }) => {
  const { t } = useTranslation();

  const { control, trigger, formState: { errors } } = useFormContext();

  const { fields, append, remove } = useFieldArray({
    control,
    name,
  });

  // useFieldArray only tracks item-level rules (e.g. each choice's title); the
  // array's own "must not be empty" constraint is registered separately below
  // via an invisible Controller on the same path, so it's a real part of RHF's
  // validation graph (survives revalidation, actually blocks handleSubmit) -
  // an imperative setError() on an unregistered path gets silently dropped on
  // the next validation pass. `trigger` just forces the message to update live
  // as soon as an option is added/removed, instead of only at submit time.
  useEffect(() => {
    trigger(name as any);
    // eslint-disable-next-line
  }, [fields.length, name]);

  const handleAdd = () => {
    append({ ref: uuidv4(), title: '' });
  }

  const handleRemove = (index:number) => () => {
    remove(index);
  }

  const choicesError = get(errors, name);

  return (
    <div className="pl-4 mb-4">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <span className="block text-gray-400 text-sm font-light leading-5">{t('pages.quizForm.choicesLabel')}</span>
        <Button variant="outlined" onClick={handleAdd}>
          {t('buttons.addOption')}
        </Button>
      </div>
      <Controller
        control={control}
        name={name as any}
        rules={{
          validate: (value:any) => (Array.isArray(value) && value.length > 0) || t('validations.choicesRequired'),
        }}
        render={() => <></>}
      />
      {choicesError ? (
        <span className="block text-red-500 text-xs font-light my-2">{choicesError.message}</span>
      ) : null}
      {fields.map((choice:any, index:number) => {
        const removeDisabled = fields.length <= minItems;

        return (
          <div key={`choice-item-${choice.id}`} className="flex gap-2">
            <div className="flex-grow">
              <MultiLanguageTextField
                name={`${name}.${index}.title`}
                rules={{ required: isRequired, validate: noWhitespace }}
                label={`${itemLabel || t('labels.option')} ${index + 1}`}
                className="mb-4"
                required
                error={Boolean(get(errors, `${name}.${index}.title`))}
                errorText={get(errors, `${name}.${index}.title.message`) || ''}
                customKey={`registrationPage.fields.${fieldRef}.choices.${choice.ref}.title`}
              />
            </div>
            <div className="mt-6">
              <Tooltip title={removeDisabled ? '' : t('buttons.remove')}>
                <IconButton
                  disabled={removeDisabled}
                  onClick={handleRemove(index)}
                ><FaTrash /></IconButton>
              </Tooltip>
            </div>
          </div>
        );
      })}
    </div>
  )
}

export default ChoicesEditor;
